import { Component, inject, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { USER_PERMISSIONS } from '../../app.constants';
import { LocalStorageService } from '../../services/local-storage.service';
import { Subscription, interval, takeWhile } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpServiceService } from '../../services/http-service.service';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { LoadingService } from '../../services/loading.service';
import { NgbAccordionModule, NgbCarouselModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-view-inspection',
  imports: [BreadcrumbComponent, NgFor, FormsModule, ReactiveFormsModule, NgIf, NgbCarouselModule, NgClass, NgbAccordionModule, DatePipe],
  templateUrl: './view-inspection.component.html',
  styleUrl: './view-inspection.component.css'
})
export class ViewInspectionComponent implements OnInit, OnDestroy{
  breadcrumbList = {
    breadcrumb_path: 'Inspections',
    currentURL: 'View Inspections',
    breadcrumb_url: '/admin/inspections'
  }
  loadingService = inject(LoadingService);
  private authService = inject(LocalStorageService);
  private queryParamsSubsscription?: Subscription;
  private getRoomInspectionsSubscription?: Subscription;
  private routeSubscription?: Subscription;
  private getPropertyDetailsByIdSubscription?: Subscription;
  private getInspectionDetailsByIdSubscription?:Subscription;
  private updateInspectionStatusSubscription?: Subscription;
  private downloadSubscription?: Subscription;
  private shareSubscription?: Subscription;
  private queryParamsData : any;
  inspectionId: string = '';
  inspectionsList : any = [];
  property_id : string = '';
  propertyDetails : any;
  inspectionDetails : any;
  acceptTerms = false;
  inspectionImages : any;
  private modalService= inject(NgbModal);

  // Download & Share state
  isDownloading: boolean = false;
  downloadProgress: string = '';
  activeDownloadType: 'download' | 'email' | 'whatsapp' | null = null;

  // Share modal state
  showShareModal: boolean = false;
  shareType: 'email' | 'whatsapp' = 'email';
  shareForm!: FormGroup;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private httpService: HttpServiceService,
    private alertService: NgxToastrService,
    private fb: FormBuilder
  ){
    this.initShareForm();
  }

  ngOnInit(): void {
    this.routeSubscription = this.route.paramMap.subscribe(params => {
      this.inspectionId = params.get('id') ?? "";
      this.property_id = params.get('property_id') ?? "";
    });
    this.queryParamsSubsscription = this.route.queryParams.subscribe((data: any) => {
      this.queryParamsData = data;
    });
    this.getInspectionRooms();
    this.getPropertyDetailsById();
  }

  private initShareForm(): void {
    this.shareForm = this.fb.group({
      first_name: [''],
      last_name: [''],
      email: [''],
      country_code: [''],
      whatsapp: ['']
    });
  }

  getInspectionRooms(){
    this.getRoomInspectionsSubscription = this.httpService.getRoomsByInspectionId(this.inspectionId).subscribe({
      next: (response) => {
         this.inspectionsList = response.data;
      }
    });
  }

  getPropertyDetailsById(){
    this.getPropertyDetailsByIdSubscription = this.httpService.getPropertyDetailsById(this.property_id).subscribe({
      next : (response) => {
        this.propertyDetails = response.data;
      }
    });
    this.getInspectionDetailsByIdSubscription  = this.httpService.getInspectionDetailsById(this.inspectionId).subscribe({
      next : (response) => {
        this.inspectionDetails = response.data;
      }
    });
  }

  viewPropertyImage(content: TemplateRef<any>, images : any = []){
      this.inspectionImages = images;
      this.modalService.open(content, { centered: true,  backdrop: 'static' });
  }

  viewImage(url : string){
    window.open(url , "_blank");
  }

  closeModal() {
    this.modalService.dismissAll();
  }

  backToInspections(data: any) {
    this.router.navigate(['admin', 'inspections'],{
      queryParams : this.queryParamsData
    });
  }

  onSubmit(){
    if(!this.acceptTerms) return;
    this.updateInspectionStatusSubscription = this.httpService.updateInspectionStatus(this.inspectionId, {}).subscribe({
      next : (response) => {
        this.inspectionDetails['showSubmit'] = !this.acceptTerms;
        this.alertService.success(response.message, "toast-top-right");
      }
    });
  }

  // Download PDF directly
  downloadPdf(): void {
    if (this.isDownloading) return;

    this.isDownloading = true;
    this.activeDownloadType = 'download';
    this.downloadProgress = 'Downloading...';

    this.downloadSubscription = this.httpService.downloadInspection(this.inspectionId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `inspection_${this.inspectionId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.resetDownloadState();
        this.alertService.success('PDF downloaded successfully!', 'toast-top-right');
      },
      error: () => {
        this.resetDownloadState();
        this.alertService.error('Failed to download PDF', 'toast-top-right');
      }
    });
  }

  private resetDownloadState(): void {
    this.isDownloading = false;
    this.downloadProgress = '';
    this.activeDownloadType = null;
  }

  // Share modal methods
  openShareModal(type: 'email' | 'whatsapp'): void {
    this.shareType = type;
    this.shareForm.reset();
    this.showShareModal = true;
  }

  closeShareModal(): void {
    this.showShareModal = false;
    this.shareForm.reset();
  }

  submitShare(): void {
    const formValue = this.shareForm.getRawValue();
    const shareValue = this.shareType === 'email'
      ? formValue.email
      : formValue.whatsapp;

    if (!shareValue || shareValue.trim() === '') {
      this.alertService.error(
        `Please enter ${this.shareType === 'email' ? 'an email address' : 'a WhatsApp number'}`,
        'toast-top-right'
      );
      return;
    }

    this.closeShareModal();
    this.isDownloading = true;
    this.activeDownloadType = this.shareType;
    this.downloadProgress = 'Sending...';

    const shareData: any = {
      share_type: this.shareType,
      first_name: formValue.first_name?.trim() || '',
      last_name: formValue.last_name?.trim() || ''
    };

    if (this.shareType === 'email') {
      shareData.email = shareValue.trim();
    } else {
      shareData.country_code = formValue.country_code?.trim() || '';
      shareData.whatsapp = shareValue.trim();
    }

    this.shareSubscription = this.httpService.shareInspectionPdf(this.inspectionId, shareData).subscribe({
      next: (response: any) => {
        if (response.success && response.data?.job_id) {
          this.pollJobStatus(response.data.job_id);
        } else {
          this.resetDownloadState();
          this.alertService.error('Failed to initiate share', 'toast-top-right');
        }
      },
      error: () => {
        this.resetDownloadState();
        this.alertService.error('Failed to initiate share', 'toast-top-right');
      }
    });
  }

  private pollJobStatus(jobId: string): void {
    const maxPollingTime = 30000;
    const pollingInterval = 1000;
    const startTime = Date.now();
    let pollCount = 0;

    // Make immediate first poll
    this.checkJobStatus(jobId);

    const poll$ = interval(pollingInterval).pipe(
      takeWhile(() => {
        const elapsed = Date.now() - startTime;
        return elapsed < maxPollingTime && this.isDownloading;
      })
    );

    poll$.subscribe({
      next: () => {
        pollCount++;
        this.downloadProgress = `Processing... (${pollCount})`;
        this.checkJobStatus(jobId);
      },
      complete: () => {
        if (this.isDownloading) {
          this.resetDownloadState();
          this.alertService.error('Share request timed out. Please try again.', 'toast-top-right');
        }
      }
    });
  }

  private checkJobStatus(jobId: string): void {
    this.httpService.getReportJobStatus(jobId).subscribe({
      next: (response: any) => {
        if (response.success && response.data) {
          const jobData = response.data;

          if (jobData.status === 'completed') {
            this.resetDownloadState();
            this.alertService.success(
              `Inspection report shared via ${this.shareType} successfully!`,
              'toast-top-right'
            );
          } else if (jobData.status === 'failed') {
            this.resetDownloadState();
            this.alertService.error(
              jobData.error_message || 'Failed to share inspection report',
              'toast-top-right'
            );
          }
        }
      },
      error: () => {
        // Continue polling on error
      }
    });
  }

  // Permission checks
  get checkDownloadPermission(): boolean {
    return this.authService.checkPermission(USER_PERMISSIONS.INSPECTIONS.INSPECTION_DOWNLOAD);
  }

  get checkAddInspection() {
      return this.authService.checkPermission(USER_PERMISSIONS.INSPECTIONS.INSPECTION_ADD);
  }

  get checkLocationView(): boolean {
    return this.authService.checkPermission(USER_PERMISSIONS.INSPECTIONS.INSPECTION_VIEW_LOCATION);
  }

  ngOnDestroy(): void {
    this.queryParamsSubsscription?.unsubscribe();
    this.getRoomInspectionsSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
    this.getPropertyDetailsByIdSubscription?.unsubscribe();
    this.getInspectionDetailsByIdSubscription?.unsubscribe();
    this.updateInspectionStatusSubscription?.unsubscribe();
    this.downloadSubscription?.unsubscribe();
    this.shareSubscription?.unsubscribe();
  }
}
